import {
	AfterViewInit,
	ChangeDetectionStrategy,
	ChangeDetectorRef,
	Component,
	ElementRef,
	EventEmitter,
	HostListener,
	Inject,
	Input,
	NgZone,
	OnChanges,
	OnDestroy,
	OnInit,
	Output,
	Renderer2,
	SimpleChanges,
	ViewChild,
	ViewRef
} from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { TranslatePipe } from '@ngx-translate/core';
import { UntypedFormControl, UntypedFormGroup, Validators } from '@angular/forms';
import {
	BehaviorSubject,
	catchError,
	delay,
	distinctUntilChanged,
	filter,
	finalize,
	interval,
	noop,
	Observable,
	of,
	Subject,
	Subscription,
	take,
	takeUntil,
	EMPTY,
	map
} from 'rxjs';
import { MatDialog } from '@angular/material/dialog';
import { CommentModel, commentModelToken } from 'go-modules/models/comment/comment.factory';
import { UserService } from 'go-modules/models/user/user.service';
import { userServiceToken } from 'go-modules/models/user/user.service';
import { GoToastStatusType } from '@go-modules/enums/go-toast-status-type';
import { NgxGoToastService } from '@go-modules/services/go-toast/go-toast.service';
import { goModal, goModalToken } from 'go-modules/modals/go-modal.factory';
import { umcChooserToken, UniversalMediaChooserFactory } from 'go-modules/universal-media-chooser/umc.factory';
import {
	CONTENT_TYPES
} from '@go-modules/components/library/library-collections-viewer/library-collection-viewer.constants';
import { COMMENT_RESOURCE_TYPES } from 'go-modules/models/comment/comment.constant';
import { ConfirmDialogComponent } from '@go-modules/components/dialogs/confirm-dialog/confirm-dialog.component';
import { NgxAuthService } from '@go-modules/services/auth/auth.service';
import { NgxCommentService } from '@go-modules/services/comment/comment.service';
import * as dayjs from 'dayjs';
import type {
	UnsavedDataRegistration
} from '@go-modules/services/unsaved-data-manager/unsaved-data-manager.service';
import {
	NgxUnsavedDataManagerService
} from '@go-modules/services/unsaved-data-manager/unsaved-data-manager.service';
import { clientSettings } from 'go-modules/models/common/client.settings';
import { EventService } from '@go-modules/services/event/event.service';
import { EVENT_NAMES } from '@go-modules/services/event/event-names.constants';
import { NgxRichTextEditorComponent } from '@go-modules/components/rich-text-editor/rich-text-editor.component';
import { MessageDialogComponent } from '@go-modules/components/dialogs/message-dialog/message-dialog.component';
import { MatMenuTrigger } from '@angular/material/menu';
import { activityToken } from 'go-modules/models/activity/activity.factory';
import { VideoQualityUtil } from '@go-modules/utilities/video-quality/video-quality.util';
import { SelectedInterface, selectedServiceToken } from 'go-modules/services/selected/selected.service';
import type {
	Sentence
} from '@go-modules/components/feedback-session/transcript-viewer/transcript-viewer.component';
import { CommentListService } from '@go-modules/services/comment-list/comment-list.service';
import { v4 as uuid } from 'uuid';
import { ImprovedCommentResponse } from '@go-modules/services/llm-task/llm-task.service';
import { GoPulseTourService } from '@go-modules/services/go-pulse-tour/go-pulse-tour.service';
import {
	COMMENT_COMPOSER_TOUR_CONFIG
} from '@go-modules/services/go-pulse-tour/constants/ai-comment-composer-tour';
import { TranscriptCacheService } from '@go-modules/services/transcript-cache/transcript-cache.service';
import type { MediaTranscription } from '@go-modules/interfaces/media-transcription';
import { FeedbackSessionService } from '@go-modules/services/feedback-session/feedback-session.service';
import { ResponsiveLayoutService } from '@go-modules/services/responsive-layout/responsive-layout.service';

export const RECENT_FEEDBACK_TYPES_STORAGE_KEY = 'recent-feedback-types';

@Component({
	selector: 'ngx-feedback-creator',
	templateUrl: './feedback-creator.component.html',
	styleUrls: ['./feedback-creator.component.scss'],
	changeDetection: ChangeDetectionStrategy.OnPush,
	providers: [TranslatePipe],
	standalone: false,
	host: {
		'[class]': 'responsiveLayout.cssClassString()'
	}
})
export class NgxFeedbackCreatorComponent implements OnInit, AfterViewInit, OnDestroy, OnChanges {
	@Input() public session: any;
	@Input() public tagsList: any;
	@Input() public playerSync: any;
	@Input() public userGroup: any;
	@Input() public oldComment: any;
	@Input() public mode: any;
	@Input() public viewFrom: string;
	@Output() public onClose: EventEmitter<any> = new EventEmitter();
	@ViewChild(NgxRichTextEditorComponent, {static: false})
	public richTextEditor: NgxRichTextEditorComponent;
	@ViewChild('emojiMenuTrigger') private emojiMenuTrigger: MatMenuTrigger;
	@ViewChild('aiCommentAssistantMenuTrigger') private aiCommentAssistantMenuTrigger: MatMenuTrigger;
	public umc: any;
	public resources: any[] = [];
	public tagResources: any[] = [];
	public mediaResources: any[] = [];
	public recentFeedbackTypes: any[];
	public numberOfRecentFeedbackTypes = 2;
	public pendingRecentFeedbackTypes: any[] = [ ];
	public typesAreUpdating: boolean = false;
	public feedbackTypesEnum = {
		VIDEO: 'video',
		UPLOAD: 'upload',
		LIBRARY: 'library',
		AUDIO: 'audio',
		YOUTUBE: 'youtube',
		ZOOM: 'zoom'
	};
	public feedbackTypes = [
		{
			type: this.feedbackTypesEnum.VIDEO,
			icon: 'ficon-app-camcorder',
			label: 'universal-media-chooser_record'
		},
		{
			type: this.feedbackTypesEnum.UPLOAD,
			icon: 'ficon-upload-outlined',
			label: 'universal-media-chooser_upload'
		},
		{
			type: this.feedbackTypesEnum.LIBRARY,
			icon: 'ficon-app-library',
			label: 'universal-media-chooser_library'
		},
		{
			type: this.feedbackTypesEnum.AUDIO,
			icon: 'ficon-microphone-outlined',
			label: 'universal-media-chooser_audio'
		},
		{
			type: this.feedbackTypesEnum.YOUTUBE,
			icon: 'ficon-youtube-icon',
			label: 'universal-media-chooser_youtube'
		},
		{
			type: this.feedbackTypesEnum.ZOOM,
			icon: 'ficon-zoom-icon',
			label: 'universal-media-chooser_zoom'
		}
	];
	public umcHeaderOptions = [
		this.UniversalMediaChooser.MEDIA_TYPE.RECORD_VIDEO,
		this.UniversalMediaChooser.MEDIA_TYPE.UPLOAD,
		this.UniversalMediaChooser.MEDIA_TYPE.LIBRARY,
		this.UniversalMediaChooser.MEDIA_TYPE.RECORD_AUDIO,
		this.UniversalMediaChooser.MEDIA_TYPE.YOUTUBE,
		this.UniversalMediaChooser.MEDIA_TYPE.ZOOM
	];
	public expandedView: boolean = false;
	public isMobile = toSignal(this.responsiveLayout.isMobileSize$, { initialValue: false });
	public isMobileCollapsed$ = new BehaviorSubject<boolean>(false);
	public characterLimit: number = clientSettings.CommentTextCharacterLimit;
	public characterMaxExceeded: boolean = false;
	public amountExceededBy: number = 0;
	public form = new UntypedFormGroup({
		commentText: new UntypedFormControl('', [Validators.required])
	});
	public time: number = 0;
	public llmTaskId: number;

	public saving$: Observable<boolean> = of(false);
	public addEndnotes: boolean = false;
	public pausedInitially: boolean = false;
	public activity;
	public isSelfDemo: boolean = false;
	public emojiFilter;
	public transcript$: Observable<MediaTranscription> = of(null);

	public unsavedDataRegistration: UnsavedDataRegistration = {
		isInUnsavedState: () => this.isUnsaved(),
		resolve: () => {
			const data = {
				title: this.translationPipe.transform('feedback-session-feedback-creator_comment_warn-on-exit-title'),
				message: this.translationPipe.transform('feedback-session-feedback-creator_comment_warn-on-exit-message'),
				confirmText: this.translationPipe.transform('feedback-session-feedback-creator_comment_warn-on-exit-resolve-btn'),
				cancelText: this.translationPipe.transform('feedback-session-feedback-creator_comment_warn-on-exit-reject-btn')
			};
			return new Promise((resolve, reject) => {
				this.ngZone.run(() => {
					const dialogRef = this.dialog.open(ConfirmDialogComponent, { data });
					dialogRef.afterClosed().subscribe((confirm) => {
						if (confirm) {
							return reject(new Error('FeedbackCreator::unsavedDataRegistration'));
						}
						return resolve();
					});
				});
			});
		}
	};
	private tagOverflowResizeObserver: ResizeObserver;
	private commentFooterResizeObserver: ResizeObserver;
	private globalEventsListener: Subscription;
	private componentDestroyed: Subject<boolean> = new Subject();

	constructor (
		// Because this uses onPush, there is no real clean way to trigger
		// change detection without being able to markForCheck or call detectChanges
		// If changing any of the input properties
		public cdr: ChangeDetectorRef,
		public unsavedDataManagerService: NgxUnsavedDataManagerService,
		@Inject(userServiceToken) private userService: UserService,
		@Inject(commentModelToken) private Comment: ReturnType<typeof CommentModel>,
		@Inject(goModalToken) private goModalService: ReturnType<typeof goModal>,
		@Inject(activityToken) private activityFactory,
		@Inject(selectedServiceToken) private selectedService,
		@Inject(umcChooserToken) private UniversalMediaChooser: ReturnType<typeof UniversalMediaChooserFactory>,
		@Inject('Window') private window: Window,
		private commentService: NgxCommentService,
		private elementRef: ElementRef,
		private renderer2: Renderer2,
		private dialog: MatDialog,
		private ngZone: NgZone,
		private translationPipe: TranslatePipe,
		private ngxGoToastService: NgxGoToastService,
		private authService: NgxAuthService,
		private eventService: EventService,
		private commentListService: CommentListService,
		private goPulseTourService: GoPulseTourService,
		private transcriptCacheService: TranscriptCacheService,
		private feedbackSessionService: FeedbackSessionService,
		public responsiveLayout: ResponsiveLayoutService
	) {}

	@HostListener('window:beforeunload', ['$event'])
	public onBeforeUnload ($event) {
		this.unsavedDataManagerService.checkUnload($event);
	}

	public ngOnInit (): void {
		this.isSelfDemo = this.authService.getCookie('is_self_demo_experiment') === 'true';
		this.umc = this.UniversalMediaChooser.get('umcModal');
		this.unsavedDataManagerService.register(this.unsavedDataRegistration);
		this.activity = this.activityFactory.model(this.session.activity);
		if (this.shouldHideLibraryFromFeedback()) {
			this.umcHeaderOptions.splice(
				this.umcHeaderOptions.indexOf(this.UniversalMediaChooser.MEDIA_TYPE.LIBRARY), 1);
			this.feedbackTypes = this.feedbackTypes.filter((feedbackType) => {
				return feedbackType.type !== this.feedbackTypesEnum.LIBRARY;
			});
		}

		if (this.shouldHideZoomFromFeedback()) {
			this.feedbackTypes = this.feedbackTypes.filter((feedbackType) =>
				feedbackType.type !== this.feedbackTypesEnum.ZOOM);
		}

		this.emojiFilter = this.commentListService.emojiFilter;

		// Set initial recent feedback types
		let sessionFeedbackTypes = JSON.parse(localStorage.getItem(RECENT_FEEDBACK_TYPES_STORAGE_KEY));
		if(sessionFeedbackTypes) {
			if (this.shouldHideLibraryFromFeedback()) {
				sessionFeedbackTypes = sessionFeedbackTypes.filter((feedbackType) => {
					return feedbackType.type !== this.feedbackTypesEnum.LIBRARY;
				});

				if (sessionFeedbackTypes.length < this.numberOfRecentFeedbackTypes) {
					const itemsNotOnRecentFeedbackList = this.feedbackTypes.filter((feedbackType) =>
						!sessionFeedbackTypes.some((_feedbackType) => feedbackType.type === _feedbackType.type)
					);
					sessionFeedbackTypes = sessionFeedbackTypes.concat(itemsNotOnRecentFeedbackList);
				}
			}

			if (this.shouldHideZoomFromFeedback()) {
				sessionFeedbackTypes = sessionFeedbackTypes.filter((feedbackType) =>
					feedbackType.type !== this.feedbackTypesEnum.ZOOM);
			}

			this.recentFeedbackTypes = JSON.parse(JSON.stringify(sessionFeedbackTypes))
				.slice(0, this.numberOfRecentFeedbackTypes);
			this.cdr.detectChanges();
		} else {
			this.recentFeedbackTypes = JSON.parse(JSON.stringify(this.feedbackTypes))
				.slice(0, this.numberOfRecentFeedbackTypes);
			this.cdr.detectChanges();
			localStorage.setItem(
				RECENT_FEEDBACK_TYPES_STORAGE_KEY,
				JSON.stringify(this.recentFeedbackTypes)
			);
		}


		// Handle when editing an existing comment
		if (this.mode !== 'add') {
			this.form.controls.commentText.setValue(this.oldComment.text || '');
			if(this.oldComment.resource) {
				this.oldComment.resource.forEach((res) => {
					this.resources.push(res);
					if (res.resource_type === COMMENT_RESOURCE_TYPES.TAG) {
						this.tagResources.push(res);
					} else if (res.resource_type === COMMENT_RESOURCE_TYPES.MEDIA) {
						this.mediaResources.push(res);
					}
				});
			}
			this.expandCommentCreator();
		} else {
			this.form.get('commentText')
				.valueChanges
				.pipe(
					distinctUntilChanged(),
					filter((value) => value?.length)
				)
				.subscribe(() => {
					if (!this.pausedInitially) {
						this.pausedInitially = true;
						this.playerSync.halt();
						if (!this.addEndnotes && !this.time) {
							this.time = this.playerSync.getTime();
							this.cdr.detectChanges();
						}
					}
				});
		}

		this.eventService.listen(EVENT_NAMES.FEEDBACK_SESSION_COMMENT_INSERT_QUOTE)
			.pipe(takeUntil(this.componentDestroyed))
			.subscribe((event) => {
				const sentence: Sentence = event.data.sentence;
				this.expandCommentCreator();
				this.addTimestamp(sentence.start);

				if (this.form.controls.commentText.value) {
					this.form.controls.commentText.setValue(`${this.form.controls.commentText.value}<br /><em>"${sentence.parsedText}"</em><br />`);
				} else {
					this.form.controls.commentText.setValue(`<em>"${sentence.parsedText}"</em><br />`);
				}
				this.richTextEditor.focusOnField();
			});

		this.selectedService.selectedSubject
			.pipe(
				takeUntil(this.componentDestroyed),
				filter((selected: SelectedInterface) => {
					return this.userGroup.hasInstructorRole(true) &&
						selected.license?.salesforce_license.comment_composer_enabled;
				}),
				take(1)
			)
			.subscribe(() => {
				// must wait until license is loaded
				this.transcript$ = this.transcriptCacheService.getTranscription({
					media: this.session.media
				});

				this.goPulseTourService.startTour(COMMENT_COMPOSER_TOUR_CONFIG)
					.pipe(
						catchError(() => EMPTY)
					)
					.subscribe();
			});
	}

	public onEditorFocusOut (editorContent: string): void {
		// update stale form value
		if (editorContent !== this.form.controls.commentText.value && editorContent.trim().length > 0) {
			this.form.controls.commentText.setValue(editorContent);
		}
	}

	public ngAfterViewInit (): void {
		this.observeSize();
	}

	public ngOnChanges (changes: SimpleChanges): void {
		if (changes.tagsList) {
			setTimeout(() => {
				this.checkTagsOverflow();
			});
		}
	}

	public ngOnDestroy (): void {
		this.tagOverflowResizeObserver.disconnect();
		this.commentFooterResizeObserver.disconnect();
		this.unsavedDataManagerService.unregister(this.unsavedDataRegistration);
		this.componentDestroyed.next(true);
		this.componentDestroyed.complete();
		this.globalEventsListener?.unsubscribe();
	}

	public expandCommentCreator (resetAddEndNote = false) {
		if(!this.expandedView) {
			this.expandedView = true;

			if(resetAddEndNote) {
				this.addEndnotes = false;
			}

			this.cdr.detectChanges();
			this.listenGlobalEvents();
		} else {
			this.richTextEditor.focusOnField();
		}
	}

	public collapseCommentCreator () {
		if(this.expandedView) {
			this.expandedView = false;
			this.pausedInitially = false;
			this.llmTaskId = null;
			this.playerSync.resume();
			if(!(this.cdr as ViewRef).destroyed) {
				this.cdr.detectChanges();
			}
			this.globalEventsListener.unsubscribe();
		}
	}

	public toggleMobileCollapse (): void {
		this.isMobileCollapsed$.next(!this.isMobileCollapsed$.getValue());
	}

	public removeTimestamp () {
		const previousTime = this.time;
		this.time = null;
		this.addEndnotes = true;
		this.ngxGoToastService.createToast({
			type: GoToastStatusType.WARNING,
			message: 'feedback-creator_timestamp-removed',
			undo: () => this.addTimestamp(previousTime)
		});
	}

	public addTimestamp (time?: number) {
		this.time = time ?? this.playerSync.getTime();
		this.addEndnotes = false;
		this.cdr.detectChanges();
	}

	public closeEditor () {
		if (this.isUnsaved()) {
			const data = {
				title: this.translationPipe.transform('common_discard-changes') + '?',
				message: this.translationPipe.transform('feedback-creator_discard-message'),
				confirmText: this.translationPipe.transform('common_discard-changes'),
				cancelText: this.translationPipe.transform('common_cancel')
			};
			const dialogRef = this.dialog.open(ConfirmDialogComponent, { data });
			dialogRef.afterClosed().subscribe((confirm) => {
				if (confirm) {
					this.onClose.emit();
				}
			});
		} else {
			this.onClose.emit();
		}
	}

	public focusSearchField () {
		interval(100)
			.pipe(
				take(1),
				delay(1)
			).subscribe(() => {
				const searchField: HTMLElement = document.getElementById('emoji-mart-search-1');
				searchField.focus();
			});
	}

	public handleKeyDownEmojiPopup ($event) {
		if ($event.key === 'Escape') {
			this.emojiMenuTrigger.closeMenu();
		} else {
			$event.stopPropagation();
		}
	}

	public addEmoji (event) {
		this.form.controls.commentText.setValue(this.form.controls.commentText.value + event.emoji.native);
		this.emojiMenuTrigger.closeMenu();
		if (this.expandedView) {
			this.richTextEditor.focusOnField();
		} else {
			this.expandCommentCreator();
		}
	}

	public selectFeedbackOption (mediaType: string) {
		this.time = this.playerSync.getTime();
		let appendTo = null;
		let headerOptions = this.umcHeaderOptions;
		if (mediaType === this.feedbackTypesEnum.VIDEO) {
			headerOptions = [
				this.UniversalMediaChooser.MEDIA_TYPE.RECORD_VIDEO
			];
			appendTo = 'ngx-feedback-creator';
		} else if (mediaType === this.feedbackTypesEnum.AUDIO) {
			headerOptions = [
				this.UniversalMediaChooser.MEDIA_TYPE.RECORD_AUDIO
			];
			appendTo = 'ngx-feedback-creator';
		}
		this.goModalService.open({
			modal: 'umc',
			modalData: {
				options: {
					appendTo,
					defaultSection: this.getDefaultSection(mediaType),
					headerOptions,
					chooseAutomatically: true,
					useMenu: false,
					resourceType: 'comment',
					resourceId: this.session.session_id,
					groupId: this.session.group_id,
					blacklist: 'fine-uploader/src/partials/previewable-only-whitelist.json',
					goRecorder: {
						warnOnExit: true,
						videoQuality: this.selectedService.getLicense()?.salesforce_license.video_quality ??
						VideoQualityUtil.MINIMUM_RESOLUTION
					},
					libraryOptions: {
						filterType: [CONTENT_TYPES.MEDIA, CONTENT_TYPES.DOCUMENTS],
						previewable: true,
						collectionManagement: false
					}
				}
			},
			animation: false
		}).result
			.then((mediaComment) => {
				const mediaExists = this.resources.some((res) => {
					return res.resource_id === mediaComment.media_id &&
						res.resource_type === COMMENT_RESOURCE_TYPES.MEDIA;
				});
				if (mediaExists) {
					const data = {
						title: this.translationPipe.transform('feedback-creator_media-in-use-title'),
						message: this.translationPipe.transform('feedback-creator_media-in-use-message'),
						confirmText: this.translationPipe.transform('common_ok')
					};
					this.dialog.open(MessageDialogComponent, { data });
					return;
				}
				this.resources.push({
					resource_type: COMMENT_RESOURCE_TYPES.MEDIA,
					resource_id: mediaComment.media_id,
					item: mediaComment
				});
				this.mediaResources = this.resources.filter((res) => {
					return res.resource_type === COMMENT_RESOURCE_TYPES.MEDIA;
				});
				this.cdr.detectChanges();
				this.updateRecentFeedbackTypes();
				if (this.expandedView) {
					this.richTextEditor.focusOnField();
				} else {
					this.expandCommentCreator();
				}
			})
			.catch(noop);
	}

	public removeAttachment (resource) {
		const index = this.resources.findIndex((res) => {
			return res === resource;
		});
		this.resources.splice(index, 1);
		this.mediaResources = this.resources.filter((res) => {
			return res.resource_type === COMMENT_RESOURCE_TYPES.MEDIA;
		});
		this.cdr.detectChanges();
	}

	public showCloseBtn () {
		return this.mode === 'reply' || this.mode === 'edit';
	}

	public selectTag (tag: any) {
		if (!this.expandedView) {
			this.time = this.playerSync.getTime();
			if (!this.feedbackSessionService.getOneClickMarkerEnabled()) {
				this.playerSync.halt();
			}
		}

		if (this.time === null && !this.addEndnotes) {
			this.time = this.playerSync.getTime();
			this.richTextEditor.focusOnField();
			if (!this.feedbackSessionService.getOneClickMarkerEnabled()) {
				this.playerSync.halt();
			}
		}

		if (tag.tag_preset_comment) {
			if (this.form.controls.commentText.value) {
				this.form.controls.commentText.setValue(this.form.controls.commentText.value + '<br />' + tag.tag_preset_comment);
			} else {
				this.form.controls.commentText.setValue(tag.tag_preset_comment);
			}
		}

		const comment = {
			resource_type: COMMENT_RESOURCE_TYPES.TAG,
			resource_id: tag.tag_id,
			item: tag
		};

		const tagExist = this.resources.some((elem) => elem.resource_id === comment.resource_id);
		if (tagExist) {
			return;
		} else {
			this.resources.push(comment);
		}

		if (this.feedbackSessionService.getOneClickMarkerEnabled()) {
			this.submitComment();
		} else {
			this.tagResources = this.resources.filter((res) => {
				return res.resource_type === COMMENT_RESOURCE_TYPES.TAG;
			});
			this.expandCommentCreator();
		}
	}

	public removeTag (tag: any) {
		const index = this.resources.findIndex((res) => {
			return res.item.tag_id === tag.item.tag_id;
		});
		this.resources.splice(index, 1);
		this.tagResources = this.resources.filter((res) => {
			return res.resource_type === COMMENT_RESOURCE_TYPES.TAG;
		});
		this.cdr.detectChanges();
	}

	public isTagSelected (tagId: any) {
		return this.tagResources.some((cTag) => cTag.item.tag_id === tagId);
	}

	public updateCharacterCount (characterData) {
		this.characterMaxExceeded = characterData.maxExceeded;
		this.amountExceededBy = characterData.amountExceededBy;
	}

	public submitComment (eventData?: any) {
		eventData?.event.preventDefault();

		if (this.characterMaxExceeded) {
			return;
		}

		if (!this.isUnsaved()) {
			return;
		}

		let currentComment = this.oldComment;

		if (this.mode === 'add') {
			currentComment = this.Comment.newInstance({
				text: this.form.controls.commentText.value,
				time: this.time,
				fullname: this.userService.currentUser.fullname,
				created_at: dayjs(),
				created_by: this.userService.currentUser.user_id,
				session_id: this.session.session_id,
				viewed_by_me: true,
				directed_at_me: false,
				numChildren: 0,
				first_name: this.userService.currentUser.first_name,
				last_name: this.userService.currentUser.last_name,
				saving: true,
				resource: this.resources.length ? this.resources : null,
				temp_id: uuid(),
				parent_id: null,
				llm_task_id: this.llmTaskId ?? null
			});
			this.form.controls.commentText.reset('');
			this.playerSync.resume();
			this.time = null;
			this.llmTaskId = null;
			this.commentListService.addComment(currentComment);
			this.cdr.detectChanges();
		} else {
			// Unnecessary property carried from using oldComment model, causes circular error
			if (currentComment.parent) {
				delete currentComment.parent;
			}
			// prevent circular error when saving parent comment with children
			if (currentComment.children) {
				delete currentComment.children;
			}

			currentComment.resource = this.resources.length ? this.resources : null;
			currentComment.text = this.form.controls.commentText.value;
			this.collapseCommentCreator();
			this.saving$ = of(true);
		}
		this.cdr.detectChanges();

		this.commentService.save(this.session.session_id, currentComment)
			.pipe(take(1))
			.pipe(takeUntil(this.componentDestroyed))
			.pipe(catchError(() => {
				this.saving$ = of(false);
				this.form.controls.commentText.setValue(currentComment.text);
				this.expandCommentCreator();
				this.time = currentComment.time;
				this.cdr.detectChanges();
				// remove comment from list
				if (this.mode !== 'edit') {
					this.commentListService.removeTempComment(currentComment);
				}

				this.ngxGoToastService.createToast({
					type: GoToastStatusType.ERROR,
					message: 'feedback-creator_add-comment_failed'
				});
				return of(null);
			}))
			.subscribe((savedComment) => {
				this.saving$ = of(false);
				this.pausedInitially = false;
				if (savedComment) {
					// replace comment with actual comment now
					const modeledComment = this.Comment.newInstance(savedComment);
					if (currentComment.comment_id) {
						this.commentListService.replaceComment(currentComment.comment_id, modeledComment);
					} else {
						this.commentListService.replaceTempComment(currentComment.temp_id, modeledComment);
					}
					this.resources = [];
					this.mediaResources = [];
					this.tagResources = [];
					this.saveRecentFeedbackTypes();
					this.eventService.broadcast(EVENT_NAMES.FEEDBACK_SESSION_FEEDBACK_SAVED, modeledComment);
					this.cdr.detectChanges();
					this.onClose.emit();
					if (this.mode === 'add' && this.expandedView) {
						this.richTextEditor.focusOnField();
					}
				}
			});
	}

	public isUnsaved () {
		if (this.oldComment) {
			// if comment text isnt the same
			if (!this.oldComment.text) {
				// Do not return false here so that resources can be checked
				if (this.form.controls.commentText.value?.length > 0) return true;
			} else if (this.form.controls.commentText.value !== this.oldComment.text) {
				return true;
			}
			if (this.oldComment.resource) {
				// find values in old comment resources not in current resources
				const differences = this.oldComment.resource.filter((res) => {
					return !this.resources.some((res2) => {
						return res.resource_id === res2.resource_id &&
							res.resource_type === res2.resource_type;
					});
				});
				// find values in current resources not in old comment resources
				const differences2 = this.resources.filter((res) => {
					return !this.oldComment.resource.some((res2) => {
						return res.resource_id === res2.resource_id &&
							res.resource_type === res2.resource_type;
					});
				});
				//combine them
				const combinedDifferences = differences.concat(differences2);
				return combinedDifferences.length > 0;
			}
			return this.resources.length > 0;
		} else {
			// if comment text or resources length
			return this.form.controls.commentText.value?.length > 0 ||
				this.resources.length > 0;
		}
	}

	public getCommentPlainTextLength (): number {
		const rawValue = this.form.controls.commentText.value;
		const parser = new DOMParser();
		const doc = parser.parseFromString(rawValue, 'text/html');
		const plainText = doc.body.textContent;
		return plainText.length;
	}

	/**
	 * Warning: Do not use this method outside the template.
	 * Reason: The license is always null onInit since it's not done being fetched yet.
	 * This method relies on the template to retrigger this check multiple times until the license is available.
	 */
	public mayHaveComposerButton () {
		return this.expandedView
			&& this.selectedService.getLicense()?.salesforce_license.comment_composer_enabled
			&& this.userGroup.hasInstructorRole(true);
	}

	public showComposerButton$ (): Observable<boolean> {
		if (!this.mayHaveComposerButton()) {
			return of(false);
		}
		if (this.tagResources.length > 0) {
			return of(true);
		}

		return this.transcript$.pipe(
			take(1),
			map((transcript) => {
				if (transcript?.word_count > 50) {
					return this.getCommentPlainTextLength() > 2;
				}
				return this.getCommentPlainTextLength() > 30;
			}),
			catchError(() => {
				return of(this.getCommentPlainTextLength() > 30);
			})
		);
	}

	public closeAICommentAssistantMenu () {
		this.aiCommentAssistantMenuTrigger.closeMenu();
	}

	public applyImprovedComment ({ comment, llm_task_id }: ImprovedCommentResponse) {
		this.form.patchValue({
			commentText: comment
		});

		this.llmTaskId = llm_task_id;
		this.richTextEditor.focusOnField();
	}

	private getDefaultSection (mediaType: string) {
		let defaultSelection;
		switch (mediaType) {
			case this.feedbackTypesEnum.VIDEO:
				defaultSelection = this.UniversalMediaChooser.MEDIA_TYPE.RECORD_VIDEO;
				break;
			case this.feedbackTypesEnum.UPLOAD:
				defaultSelection = this.UniversalMediaChooser.MEDIA_TYPE.UPLOAD;
				break;
			case this.feedbackTypesEnum.LIBRARY:
				defaultSelection = this.UniversalMediaChooser.MEDIA_TYPE.LIBRARY;
				break;
			case this.feedbackTypesEnum.AUDIO:
				defaultSelection = this.UniversalMediaChooser.MEDIA_TYPE.RECORD_AUDIO;
				break;
			case this.feedbackTypesEnum.YOUTUBE:
				defaultSelection = this.UniversalMediaChooser.MEDIA_TYPE.YOUTUBE;
				break;
			case this.feedbackTypesEnum.ZOOM:
				defaultSelection = this.UniversalMediaChooser.MEDIA_TYPE.ZOOM;
				break;
		}
		return defaultSelection;
	}

	private listenGlobalEvents () {
		const emitter = new Subject();

		const closeOnEscapeKeyHandler = ($event: KeyboardEvent) => {
			if($event.key === 'Escape' && !this.form.controls.commentText.value
				&& this.resources.length === 0 && this.mode !== 'edit' && this.mode !== 'reply') {
				this.collapseCommentCreator();
			}
		};

		const closeOnClickOutsideHandler = ($event: PointerEvent) => {
			const inOrTheTriggerElement = this.elementRef.nativeElement.contains($event.target as Element) ||
				this.elementRef.nativeElement === $event.target;

			const element = $event.target as HTMLElement;
			const isToastElement = element.closest('.go-react-toast');
			const isTagMenuElement = element.closest('.tag-menu');
			const isUmc = element.closest('.universal-media-chooser-modal');
			const isEmojiPicker = element.closest('.emoji-menu');

			if(!inOrTheTriggerElement && !this.form.controls.commentText.value
				&& !isToastElement && !isTagMenuElement && !isUmc && this.resources.length === 0
				&& this.mode !== 'edit' && this.mode !== 'reply' &&
				!isEmojiPicker) {
				this.collapseCommentCreator();
			}
		};

		const keydownEventDestroyFn = this.renderer2.listen(window, 'keydown', closeOnEscapeKeyHandler);
		const mousedownEventDestroyFn = this.renderer2.listen(window, 'mousedown', closeOnClickOutsideHandler);

		this.globalEventsListener = emitter.asObservable().pipe(
			finalize(() => {
				emitter.complete();
				keydownEventDestroyFn();
				mousedownEventDestroyFn();
			})
		).subscribe();
	}

	/*
		Remove library when user does not have instructor roles or above
		or when app is viewed from video-sharing
	*/
	private shouldHideLibraryFromFeedback () {
		return (!this.userGroup.hasInstructorRole(true) && !this.authService.isAdmin()) || this.viewFrom === 'video-sharing';
	}

	private shouldHideZoomFromFeedback () {
		return this.window.goSettings.defaultCountryCode !== 'US';
	}

	private updateRecentFeedbackTypes () {
		let feedbackType;
		if (this.umc.currentSection.name === this.UniversalMediaChooser.MEDIA_TYPE.RECORD_VIDEO.name) {
			feedbackType = this.feedbackTypesEnum.VIDEO;
		} else {
			feedbackType = this.umc.currentSection.name.replace('universal-media-chooser_','');
		}

		if (!this.pendingRecentFeedbackTypes.length) {
			this.pendingRecentFeedbackTypes = [...this.recentFeedbackTypes];
		}

		const typeIndex = this.feedbackTypes.findIndex(
			(type) => type.type === feedbackType
		);
		const recentTypeIndex = this.pendingRecentFeedbackTypes.findIndex(
			(type) => type.type === feedbackType
		);

		if(recentTypeIndex < 0) {
			this.pendingRecentFeedbackTypes.unshift(this.feedbackTypes[typeIndex]);
			this.pendingRecentFeedbackTypes.pop();
		} else {
			this.pendingRecentFeedbackTypes.splice(recentTypeIndex, 1);
			this.pendingRecentFeedbackTypes.unshift(this.feedbackTypes[typeIndex]);
		}
	}

	private saveRecentFeedbackTypes () {
		if (this.pendingRecentFeedbackTypes.length) {
			this.typesAreUpdating = true;
			this.cdr.detectChanges();

			this.recentFeedbackTypes = [...this.pendingRecentFeedbackTypes];
			this.typesAreUpdating = false;
			this.cdr.detectChanges();

			localStorage.setItem(
				RECENT_FEEDBACK_TYPES_STORAGE_KEY,
				JSON.stringify(this.recentFeedbackTypes)
			);
		}
	}

	private checkTagsOverflow () {
		const tagsContainer = this.elementRef.nativeElement.querySelector('.tags-container');
		const markers = Array.from(tagsContainer.querySelectorAll('.marker-span'));
		const visibleMarkers = markers.filter((tag: HTMLElement) => tag.offsetTop <= (tagsContainer.offsetTop + 5));
		const overflowMarkers = markers.filter((tag: HTMLElement) => tag.offsetTop > (tagsContainer.offsetTop + 5));
		visibleMarkers.forEach((tag: HTMLElement) => tag.style.visibility = 'visible');
		overflowMarkers.forEach((tag: HTMLElement) => tag.style.visibility = 'hidden');

		const overflowButton = this.elementRef.nativeElement.querySelector('.overflow-tags-button-container');
		overflowButton.style.visibility = overflowMarkers.length > 0 ? 'visible' : 'hidden';
	}

	private observeSize () {
		const footer: HTMLDivElement = this.elementRef.nativeElement.querySelector('.comment-creator-footer');
		const sizeChange: ResizeObserverCallback = () => {
			footer.classList.toggle('resize-sensor-320', footer.offsetWidth <= 320);
			footer.classList.toggle('resize-sensor-350', footer.offsetWidth <= 350);
		};

		this.commentFooterResizeObserver = new ResizeObserver(sizeChange);
		this.commentFooterResizeObserver.observe(footer, { box: 'border-box' });

		const tagsContainer = this.elementRef.nativeElement.querySelector('.tags-container');
		this.tagOverflowResizeObserver = new ResizeObserver(() => this.checkTagsOverflow());
		this.tagOverflowResizeObserver.observe(tagsContainer, { box: 'border-box' });
	}
}
